import Vue from "vue";
import VueRouter, {Route} from "vue-router";
import rd from "./modules/vRoutedefs";
import {componentLoad} from "./modules/loading";
import NotFound from "./components/prelogin/NotFound.vue";
import {maybeNum, noNullApply, numbersFromSeparatorListed, numOrNull, nvl, requireNum} from "@/modules/utils";
import {DateTime, PhoneNumber} from "@/modules/models";
import FirstEntry from "@/components/prelogin/FirstEntry.vue";
import MissingPhoneInp from "@/components/prelogin/MissingPhoneInp.vue";
import KeyLogin from "@/components/prelogin/KeyLogin.vue";
import TitledRoute from "@/modules/TitledRoute";
import BaseApi from "@/store/modules/base";
import {CanonicalHost} from "@/modules/constants";
import LoginForm from "@/components/prelogin/LoginForm.vue";
import RegistrationsPageRouteParams, {DateMode} from "@/components/registrations/RegistrationsPageRouteParams";

Vue.use(VueRouter);

const redirPropSet = (route: Route) => ({
    redir: route.query.redir || "",
});
const phoneAndRedirPropSet = (route: Route) => {
    const phone = new PhoneNumber(requireNum(route.params.countrycode), requireNum(route.params.number));
    return Object.assign(redirPropSet(route), {
        phone: phone
    });
};
const phoneCheckedPropSet = (route: Route) => {
    const maybeTimestamp = (qv: any) => noNullApply(numOrNull(qv), DateTime.fromTimestamp);
    const pendingCreated = maybeTimestamp(route.query.pendingCreated);
    const confirmedCreated = maybeTimestamp(route.query.confirmedCreated);
    return Object.assign(phoneAndRedirPropSet(route), {
        pendingCreated: pendingCreated,
        confirmedCreated: confirmedCreated,
    });
};
const verifiedPropSet = (route: Route) => {
    return Object.assign(phoneAndRedirPropSet(route), {
        cardnum: requireNum(route.params.cardnum),
    });
};

const registrationsPageParams = (route: Route) => {
    // Parse from, to, and rest of route params
    const fug = typeof route.query.filterUserGuids == "string" ? route.query.filterUserGuids.split(",").filter(r => r.length > 0) : [];
    const params = new RegistrationsPageRouteParams(
        noNullApply(route.query.from, DateTime.fromDayParam),
        noNullApply(route.query.to, DateTime.fromDayParam),
        nvl(numOrNull(route.query.dateMode, true), DateMode.single),
        numbersFromSeparatorListed(route.query.filterLocationIds),
        numbersFromSeparatorListed(route.query.filterOrgnums),
        nvl(numOrNull(route.query.page, true), 1),
        nvl(numOrNull(route.query.ordering, true), -2),
        fug,
    );
    return {
        routeParams: params
    };
}

const cardExpirationReportParams = (route: Route) => {
    const maxExpiration: DateTime | null = noNullApply(route.query["max-expiration"], DateTime.fromDayParam);
    const minTimeIn: DateTime | null = noNullApply(route.query["min-time-in"], DateTime.fromDayParam);
    const params = {}
    if(maxExpiration) {
        params["maxExpiration"] = maxExpiration;
    }
    if(minTimeIn) {
        params["minTimeIn"] = minTimeIn;
    }
    return params;
}

// If current url host is not the canonical host, add canonical link pointint to correct host
// So that google search result at.proresult.no => anleggsterminal.no transition happens.
const setCanonical = (to: Route) => {
    const canonicalHref = `https://${CanonicalHost}${to.fullPath}`;
    const existing = document.querySelector(`link[rel="canonical"]`);
    if(existing != null) {
        existing.setAttribute("href", canonicalHref);
    } else {
        const newLink = document.createElement("link");
        newLink.rel = "canonical";
        newLink.href = canonicalHref;
        if(document.head) document.head.appendChild(newLink);
    }
};

export const routes: TitledRoute[] = [
    {path: "", meta: {title: rd.start.title}, redirect: rd.start.path}, // <- should be changed later
    {name: rd.start.name, path: rd.start.path, meta: {title: rd.start.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/UserOverview.vue"))},
    {name: rd.start.me.name, path: rd.start.me.path, meta: {title: rd.start.me.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/UserCardContainer.vue")), props: (route: Route) => ({triggerEmailAdd: route.query["trigger-email-add"]})},
    {name: rd.start.me.registrations.name, path: rd.start.me.registrations.path, meta: {title: rd.start.me.registrations.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/UserReghistPage.vue"))},
    {path: rd.start.card.add.path, meta: {title: rd.start.card.add.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/CardAdd.vue"))},
    {path: rd.start.card.add.qrinfo.path, meta: {title: rd.start.card.add.qrinfo.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/QrPreReader.vue"))},
    {path: rd.start.card.add.qrReader.path, meta: {title: rd.start.card.add.qrReader.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/QrCardAdd.vue"))},
    {name: rd.start.card.add.pending.name, path: rd.start.card.add.pending.path, meta: {title: rd.start.card.add.pending.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/PendingCardProcess.vue")), props: (route: Route) => ({cardNum: requireNum(route.params.cardNum)})},
    {name: rd.start.me.regreminder.name, path: rd.start.me.regreminder.path, meta: {title: rd.start.me.regreminder.title}, component: componentLoad(() => import(/* webpackChunkName: "start" */ "./components/start/UserRegreminderPage.vue"))},
    {
        name: rd.start.me.notifications.name,
        path: rd.start.me.notifications.path,
        meta: {title: rd.start.me.notifications.title},
        component: componentLoad(() => import(/* webpackChunkName: "Notification" */ "./components/start/NotificationConfigPage.vue"))
    },

    {name: rd.locations.setup.name, path: rd.locations.setup.path, meta: {title: rd.locations.setup.title}, component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/location/LocationSetupWizard.vue")), props: (route: Route) => ({locationId: maybeNum(route.query["location-id"]) || 0, pStep: maybeNum(route.query["step"]) || 1})},
    {name: rd.locations.name, path: rd.locations.path, meta: {title: rd.locations.title}, component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/location/LocationOverview.vue"))},
    {name: rd.locations.location.name, path: rd.locations.location.path, meta: {title: rd.locations.location.title}, component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/location/Location.vue")), props: (route: Route) => ({locationId: requireNum(route.params.locationId)})},
    {path: rd.locations.location.onsite.path, meta: {title: rd.locations.location.onsite.title}, component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/location/Onsite.vue")), props: (route: Route) => ({locationId: requireNum(route.params.locationId)})},
    {name: rd.locations.location.poster.name, path: rd.locations.location.poster.path, meta: {title: rd.locations.location.poster.title}, component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/location/LocationPoster.vue")), props: (route: Route) => ({locationId: requireNum(route.params.locationId)})},
    {name: rd.locations.location.confirmedHseGuidelineReport.name, path: rd.locations.location.confirmedHseGuidelineReport.path, meta: {title: rd.locations.location.confirmedHseGuidelineReport.title}, component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/location/PersonsHseGuidelineConfirmedReport.vue")), props: (route: Route) => ({locationId: requireNum(route.params.locationId)})},

    {
        name: rd.registrations.name,
        path: rd.registrations.path,
        meta: {title: rd.registrations.title},
        component: componentLoad(() => import(/* webpackChunkName: "location" */ "./components/registrations/RegistrationsPage.vue")),
        props: registrationsPageParams
    },

    {name: rd.terminals.name, path: rd.terminals.path, meta: {title: rd.terminals.title}, component: componentLoad(() => import(/* webpackChunkName: "terminals" */ "./components/terminals/TerminalOverview.vue"))},
    {name: rd.terminals.terminal.name, path: rd.terminals.terminal.path, meta: {title: rd.terminals.terminal.title}, component: componentLoad(() => import(/* webpackChunkName: "terminals" */ "./components/terminals/TerminalPage.vue")), props: true},
    {name: rd.terminals.pending.name, path: rd.terminals.pending.path, meta: {title: rd.terminals.pending.title}, component: componentLoad(() => import(/* webpackChunkName: "terminals" */ "./components/terminals/PendingTerminalPage.vue")), props: true},
    {name: rd.terminals.terminal.disconnectAlarm.name, path: rd.terminals.terminal.disconnectAlarm.path, meta: {title: rd.terminals.terminal.disconnectAlarm.title}, component: componentLoad(() => import(/* webpackChunkName: "terminals" */ "./components/terminals/TerminalDisconnectalarm.vue")), props: true},
    {name: rd.shop.name, path: rd.shop.path, meta: {title: rd.shop.title}, component: componentLoad(() => import(/* webpackChunkName: "shop" */ "./components/shop/NewOrderPage.vue")), props: (route: Route) => ({chosenLocationId: route.query["chosen-location-id"] || 0})},
    {name: rd.shop.order.name, path: rd.shop.order.path, meta: {title: rd.shop.order.title}, component: componentLoad(() => import(/* webpackChunkName: "shop" */ "./components/shop/OrderPage.vue")), props: (route: Route) => ({id: requireNum(route.params.id), chosenLocationId: route.query["chosen-location-id"] || 0})},
    {name: rd.shop.orders.name, path: rd.shop.orders.path, meta: {title: rd.shop.orders.title}, component: componentLoad(() => import(/* webpackChunkName: "shop" */ "./components/shop/OrderListingPage.vue"))},

    {name: rd.system.name, path: rd.system.path, meta: {title: rd.system.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/Index.vue"))},
    {name: rd.system.integrations.name, path: rd.system.integrations.path, meta: {title: rd.system.integrations.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/Integrations.vue"))},
    {name: rd.system.integrations.hmsreg.name, path: rd.system.integrations.hmsreg.path, meta: {title: rd.system.integrations.hmsreg.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/HmsregSettingspage.vue"))},
    {name: rd.system.integrations.proresult.name, path: rd.system.integrations.proresult.path, meta: {title: rd.system.integrations.proresult.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/ProresultSettingspage.vue"))},
    {name: rd.system.integrations.exportservice.name, path: rd.system.integrations.exportservice.path, meta: {title: rd.system.integrations.exportservice.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/ExportserviceSettingspage.vue"))},
    {name: rd.system.billing.name, path: rd.system.billing.path, meta: {title: rd.system.billing.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/BillingPage.vue"))},
    {name: rd.system.hseguidelines.name, path: rd.system.hseguidelines.path, meta: {title: rd.system.hseguidelines.title}, component: componentLoad(() => import(/* webpackChunkName: "systemsettings" */ "./components/systemsettings/HseGuidelinesPage.vue"))},
    {name: rd.docs.name, path: rd.docs.path, meta: {title: rd.docs.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/Index.vue"))},
    {path: rd.docs.intro.path, meta: {title: rd.docs.intro.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/intro/Intro1.vue"))},
    {path: rd.docs.adminintro.path, meta: {title: rd.docs.adminintro.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/intro/AdminIntro.vue"))},
    {path: rd.docs.intro.locationsetup.path, meta: {title: rd.docs.intro.locationsetup.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/intro/LocationSetup.vue"))},
    {path: rd.docs.roleinfo.path, meta: {title: rd.docs.roleinfo.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/RoleInfo.vue"))},
    {path: rd.docs.priceinfo.path, meta: {title: rd.docs.priceinfo.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/Priceinfo.vue"))},
    {path: rd.docs.iphonewebreset.path, meta: {title: rd.docs.iphonewebreset.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/IphoneWebdataReset.vue"))},
    {path: rd.docs.regreminder.path, meta: {title: rd.docs.regreminder.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/Regreminder.vue"))},
    {path: rd.docs.newcard.path, meta: {title: rd.docs.newcard.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/NewCard.vue"))},
    {path: rd.docs.notifications.path, meta: {title: rd.docs.notifications.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/Notifications.vue"))},
    {path: rd.docs.hseguidelines.path, meta: {title: rd.docs.hseguidelines.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/HseGuidelineInfo.vue"))},
    {path: rd.docs.infectiontrace.path, meta: {title: rd.docs.infectiontrace.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/infectiontrace/InfectionTraceInfoPage.vue"))},
    {path: rd.docs.terminal.path, meta: {title: rd.docs.terminal.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/terminal/Index.vue"))},
    {path: rd.docs.terminal.reactivation.path, meta: {title: rd.docs.terminal.reactivation.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/terminal/Reactivation.vue"))},
    {path: rd.docs.terminal.networksetup.path, meta: {title: rd.docs.terminal.networksetup.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/terminal/NetworkSetup.vue"))},
    {path: rd.docs.terminal.nocardread.path, meta: {title: rd.docs.terminal.nocardread.title}, component: componentLoad(() => import(/* webpackChunkName: "docs" */ "./components/docs/terminal/NoCardRead.vue"))},
    {name: rd.company.name, path: rd.company.path, meta: {title: rd.company.title}, component: componentLoad(() => import(/* webpackChunkName: "company" */ "./components/companypage/CompanyPage.vue"))},
    {
        name: rd.company.init.name,
        path: rd.company.init.path,
        meta: {title: rd.company.init.title},
        component: componentLoad(() => import(/* webpackChunkName: "company" */ "./components/companypage/AccountInit.vue")),
        props: (route: Route) => ({initOrgnum: maybeNum(route.query["init-orgnum"]) || null})
    },
    {name: rd.users.name, path: rd.users.path, meta: {title: rd.users.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/UserAdmin.vue"))},
    {name: rd.users.add.name, path: rd.users.add.path, meta: {title: rd.users.add.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/UserAdd.vue"))},
    {path: rd.users.reports.path, meta: {title: rd.users.reports.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/ReportCards.vue"))},
    {path: rd.users.reports.phonelist.path, meta: {title: rd.users.reports.phonelist.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/PhonelistReport.vue"))},
    {path: rd.users.reports.allusers.path, meta: {title: rd.users.reports.allusers.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/AllUsersReport.vue"))},
    {path: rd.users.reports.roles.path, meta: {title: rd.users.reports.roles.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/UserRoleReport.vue"))},
    {path: rd.users.reports.cardexpiration.path, redirect: rd.users.reports.cardexpirationlocation.path},
    {path: rd.users.reports.cardexpirationlocation.path, meta: {title: rd.users.reports.cardexpirationlocation.title}, props: cardExpirationReportParams, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/CardExpirationReportLocations.vue"))},
    {path: rd.users.reports.cardexpirationcompany.path, meta: {title: rd.users.reports.cardexpirationcompany.title}, props: cardExpirationReportParams, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/CardExpirationReportCompanycards.vue"))},
    {path: rd.users.reports.withoutCard.path, meta: {title: rd.users.reports.withoutCard.title}, component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/UsersWithoutCardReport.vue"))},
    {
        name: rd.users.reports.withoutPhone.name,
        path: rd.users.reports.withoutPhone.path,
        meta: {title: rd.users.reports.withoutPhone.title},
        component: componentLoad(() => import(/* webpackChunkName: "users" */ "./components/users/reports/UsersWithoutPhoneReport.vue")),
        props: (route: Route) => {
            const ret: any = {};
            if(typeof route.query.from == "string") {
                ret.from = DateTime.fromDayParam(route.query.from);
            }
            if(typeof route.query.to == "string") {
                ret.to = DateTime.fromDayParam(route.query.to);
            }
            return ret;
        }
    },

    {name: rd.usermessages.name, path: rd.usermessages.path, meta: {title: rd.usermessages.title}, component: componentLoad(() => import(/* webpackChunkName: "usermessages" */ "./components/usermessages/Usermessages.vue")), props: (route: Route) => ({archived: !!route.query.archived, page: maybeNum(route.query.page) || 1})},
    {name: rd.super.name, path: rd.super.path, meta: {title: rd.super.title}, component: componentLoad(() => import(/* webpackChunkName: "usermessages" */ "./components/super/SuperCompany.vue"))},
    {name: rd.super.sims.name, path: rd.super.sims.path, meta: {title: rd.super.sims.title}, component: componentLoad(() => import(/* webpackChunkName: "usermessages" */ "./components/super/SuperSims.vue"))},
    {name: rd.super.simsDetail.name, path: rd.super.simsDetail.path, meta: {title: rd.super.simsDetail.title}, component: componentLoad(() => import(/* webpackChunkName: "usermessages" */ "./components/super/SuperSims.vue")), props: (route: Route) => ({section: route.params.section})},
    {name: rd.super.companyMigrate.name, path: rd.super.companyMigrate.path, meta: {title: rd.super.companyMigrate.title}, component: componentLoad(() => import(/* webpackChunkName: "usermessages" */ "./components/super/SuperCompanyMigratePage.vue"))},
    {name: rd.super.usagestats.name, path: rd.super.usagestats.path, meta: {title: rd.super.usagestats.title}, component: componentLoad(() => import(/* webpackChunkName: "usagestats" */ "./components/super/SuperUsageStats.vue"))},
    {
        name: rd.super.terminalDataUsage.name,
        path: rd.super.terminalDataUsage.path,
        meta: {title: rd.super.terminalDataUsage.title},
        component: componentLoad(() => import(/* webpackChunkName: "terminalDataUsage" */ "./components/super/SuperTerminalDataUsage.vue")),
        props: (route: Route) => ({guid: route.params.guid})
    },

    {name: rd.unauthorized.name, path: rd.unauthorized.path, meta: {title: rd.unauthorized.title}, component: componentLoad(() => import(/* webpackChunkName: "unauthorized" */ "./components/UnauthorizedPage.vue"))},

    {name: rd.public.name, path: rd.public.path, meta: {title: rd.public.title}, component: FirstEntry, props: (route: Route) => ({phone: route.query.phone || null, redir: route.query.redir || null})},
    {name: rd.public.signup.name, path: rd.public.signup.path, meta: {title: rd.public.signup.title}, component: componentLoad(() => import(/*webpackChunkName: "public" */ "./components/prelogin/NewCustomer.vue"))},
    {name: rd.login.name, path: rd.login.path, meta: {title: rd.login.title}, component: LoginForm},
    {name: rd.login.missing.name, path: rd.login.missing.path, meta: {title: rd.login.missing.title}, component: MissingPhoneInp, props: (route: Route) => ({phone: route.query.phone || null, signup: route.query.signup || false, redir: route.query.redir || "", accountInit: route.query["account-init"] || false })},
    {path: "/login/privacy/policy", redirect: rd.about.privacypolicy.path },

    {name: rd.login.keylogin.name, path: rd.login.keylogin.path, meta: {title: rd.login.keylogin.title}, component: KeyLogin, props: (route: Route) => ({redir: route.query.redir})},
    {
        name: rd.login.signupunverified.name,
        path: rd.login.signupunverified.path,
        meta: {title: rd.login.signupunverified.title},
        component: componentLoad(() => import(/* webpackChunkName: "SignupUnverified" */ "./components/prelogin/SignupUnverified.vue")),
        props: redirPropSet,
    },
    {
        name: rd.login.checked.name,
        path: rd.login.checked.path,
        meta: {title: rd.login.checked.title},
        component: componentLoad(() => import(/* webpackChunkName: "signin" */ "./components/prelogin/PhoneChecked.vue")),
        props: phoneCheckedPropSet
    },
    {
        name: rd.login.signup.name,
        path: rd.login.signup.path,
        meta: {title: rd.login.signup.title},
        component: componentLoad(() => import(/* webpackChunkName: "signin" */ "./components/prelogin/SignupMethodChoice.vue")),
        props: phoneAndRedirPropSet
    },
    {
        name: rd.login.signup.qr.name,
        path: rd.login.signup.qr.path,
        meta: {title: rd.login.signup.qr.title},
        component: componentLoad(() => import(/* webpackChunkName: "signin" */ "./components/prelogin/SignupQr.vue")),
        props: phoneAndRedirPropSet
    },
    {
        name: rd.login.signup.qr.info.name,
        path: rd.login.signup.qr.info.path,
        meta: {title: rd.login.signup.qr.info.title},
        component: componentLoad(() => import(/* webpackChunkName: "signin" */ "./components/prelogin/QrStartInfo.vue")),
        props: phoneAndRedirPropSet
    },
    {
        name: rd.login.signup.verified.name,
        path: rd.login.signup.verified.path,
        meta: {title: rd.login.signup.verified.title},
        component: componentLoad(() => import(/* webpackChunkName: "SignupVerified" */ "./components/prelogin/SignupVerified.vue")),
        props: verifiedPropSet
    },
    {
        name: rd.login.config.name,
        path: rd.login.config.path,
        meta: {title: rd.login.config.title},
        component: componentLoad(() => import(/* webpackChunkName: "LoginConfig" */ "./components/prelogin/LoginConfig.vue")),
    },
    {
        name: rd.about.name,
        path: rd.about.path,
        meta: {title: rd.about.title},
        component: componentLoad(() => import(/* webpackChunkName: "About" */ "./components/about/About.vue")),
    },
    {
        name: rd.about.privacypolicy.name,
        path: rd.about.privacypolicy.path,
        meta: {title: rd.about.privacypolicy.title},
        component: componentLoad(() => import(/* webpackChunkName: "About" */ "./components/about/privacy/PrivacyPolicyInfo.vue"))
    },
    {
        name: rd.about.userterms.name,
        path: rd.about.userterms.path,
        meta: {title: rd.about.userterms.title},
        component: componentLoad(() => import(/* webpackChunkName: "About" */ "./components/about/terms/UsertermsPage.vue"))
    },
    {
        name: rd.public.emailConfirm.name,
        path: rd.public.emailConfirm.path,
        meta: {title: rd.public.emailConfirm.title},
        component: componentLoad(() => import(/* webpackChunkName: "Email" */ "./components/prelogin/EmailConfirmPage.vue")),
        props: true
    },
    {name: rd.public.videos.name, path: rd.public.videos.path, meta: {title: rd.public.videos.title}, component: componentLoad(() => import(/* webpackChunkName: "Videos" */ "./components/prelogin/Videos.vue"))},
    {name: rd.users.reports.infectiontrace.name, path: rd.users.reports.infectiontrace.path, meta: {title: rd.users.reports.infectiontrace.title}, component: componentLoad(() => import(/* webpackChunkName: "infectiontrace" */ "./components/users/reports/InfectionTracer.vue"))},
    {name: rd.public.recaptcha.name, path: rd.public.recaptcha.path, meta: {title: rd.public.recaptcha.title}, component: componentLoad(() => import(/* webpackChunkName: "Recaptcha" */ "./components/prelogin/RecaptchaCheckPage.vue"))},

    {path: "*", meta: {title: "Ikke funnet"}, component: NotFound},
];

const addRouteMetaInitDrawer = (r: Route, initDrawer: boolean) => {
    if(r.meta) {
        // Don't override it if it is already set on route
        if(r.meta.initDrawer === undefined || r.meta.initDrawer === null) {
            r.meta.initDrawer = initDrawer;
        }
    } else {
        r.meta = {
            initDrawer: initDrawer
        };
    }
};

const initRouter = (baseApi: BaseApi) => {
    const vr = new VueRouter({
        routes: routes,
        base: process.env.BASE_URL,
        mode: "history",
        scrollBehavior: (to, from, savedPosition) => {
            if(savedPosition) {
                return savedPosition;
            } else if (to.path === from.path && /^\/v\/super\/sims(?:\/|$)/.test(to.path)) {
                // Editing a SIM filter updates the URL without jumping to the top.
                return;
            } else {
                return {x: 0, y: 0};
            }
        },
    });

    vr.beforeEach((to, from, next) => {
        if(baseApi.authenticated) {
            // When user is logged in, display the nav drawer normally.
            addRouteMetaInitDrawer(to, true);
        }
        if (to.path.startsWith("/v/")) {
            if(baseApi.authenticated) {
                next();
            } else {
                next({name: rd.public.name, query: {redir: to.fullPath}});
            }
        } else {
            next();
        }
    });

    vr.afterEach((to) => {
        setCanonical(to); // update canonical link header to new route url
    });

    vr.onError((err) => {
        if(isLoadError(err)) {
            throw new RouteLoadError(err.message);
        } else {
            throw err;
        }
    });

    return vr;
};

const isRedundantNavErr = (err: Error) => err && err.message && err.message.includes("Avoided redundant navigation to current location");

export const ignoreRedundantNavErr = (routeProm: Promise<Route>): Promise<null | Route> => routeProm.catch(err => isRedundantNavErr(err) ? Promise.resolve(null) : Promise.reject(err))

const isLoadError = (err: Error) => {
    return err.message.match(/Loading .+ failed/i);
};

export class RouteLoadError extends Error {
    constructor(msg: string) {
        super(msg);
    }
}

export default initRouter;
